import React, { useState, useEffect, useMemo } from "react";
import { useFindMany, useGlobalAction } from "@gadgetinc/react";
import { api } from "../../../api";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  Text,
  Alert,
  AlertIcon,
  Spinner,
  Badge,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";

// Individual Scenario Access Section Component
function IndividualScenarioAccessSection({ institution }) {
  const toast = useToast();
  const [selectedNonCohortUser, setSelectedNonCohortUser] = useState("");

  // Fetch all users from this institution
  const [{ data: institutionUsers, fetching: loadingUsers, error: usersError }, refetchUsers] = useFindMany(api.user, {
    filter: { institution: { equals: institution } },
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      individualScenarioAccess: true,
    }
  });

  // Fetch cohort memberships to identify students in cohorts
  const [{ data: cohortMemberships, fetching: loadingMemberships }] = useFindMany(api.cohortMembership, {
    filter: {
      active: { equals: true },
      primaryRole: { equals: "student" }
    },
    select: {
      id: true,
      user: { id: true },
      primaryRole: true,
      hasScenarioGeneratorAccess: true,
    }
  });

  // Action for updating user scenario access
  const [{ data: accessUpdateData, fetching: updatingAccess, error: accessUpdateError }, updateUserScenarioAccess] = useGlobalAction(api.updateUserScenarioAccess);
  const [pendingAccessChange, setPendingAccessChange] = useState(null);

  // Monitor access update completion
  useEffect(() => {
    if (!updatingAccess && pendingAccessChange) {
      if (accessUpdateData?.success) {
        toast({
          title: "Access Updated",
          description: `Individual scenario access ${pendingAccessChange.newAccess ? 'granted' : 'revoked'}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        refetchUsers();
      } else if (accessUpdateError || accessUpdateData?.success === false) {
        toast({
          title: "Error",
          description: accessUpdateData?.error || accessUpdateError?.message || "Failed to update access",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      setPendingAccessChange(null);
    }
  }, [updatingAccess, accessUpdateData, accessUpdateError, pendingAccessChange, toast, refetchUsers]);

  // Get users with individual scenario access
  const individualAccessUsers = useMemo(() => {
    if (!institutionUsers || !Array.isArray(institutionUsers)) return [];
    return institutionUsers
      .filter(user => user.individualScenarioAccess === true)
      .sort((a, b) => {
        const lastNameA = a.lastName || '';
        const lastNameB = b.lastName || '';
        if (lastNameA !== lastNameB) {
          return lastNameA.localeCompare(lastNameB);
        }
        return (a.firstName || '').localeCompare(b.firstName || '');
      });
  }, [institutionUsers]);

  // Get non-cohort members (exclude students in active cohorts)
  const nonCohortMembers = useMemo(() => {
    if (!institutionUsers || !Array.isArray(institutionUsers)) return [];

    const studentIds = new Set(
      (cohortMemberships || [])
        .filter(m => m.user && m.primaryRole === "student")
        .map(m => m.user.id)
    );

    return institutionUsers
      .filter(user => !studentIds.has(user.id) && !user.individualScenarioAccess)
      .sort((a, b) => {
        const lastNameA = a.lastName || '';
        const lastNameB = b.lastName || '';
        if (lastNameA !== lastNameB) {
          return lastNameA.localeCompare(lastNameB);
        }
        return (a.firstName || '').localeCompare(b.firstName || '');
      });
  }, [institutionUsers, cohortMemberships]);

  // Categorize all users by their scenario access source
  const userAccessStatus = useMemo(() => {
    if (!institutionUsers || !Array.isArray(institutionUsers)) return [];

    // Create a map of userId -> cohort scenario access
    const cohortAccessMap = new Map();
    (cohortMemberships || []).forEach(membership => {
      if (membership.user && membership.hasScenarioGeneratorAccess === true) {
        cohortAccessMap.set(membership.user.id, true);
      }
    });

    return institutionUsers
      .map(user => {
        const hasCohortAccess = cohortAccessMap.has(user.id);
        const hasIndividualAccess = user.individualScenarioAccess === true;

        return {
          ...user,
          hasCohortAccess,
          hasIndividualAccess,
          hasAnyAccess: hasCohortAccess || hasIndividualAccess,
          accessSource: hasCohortAccess && hasIndividualAccess
            ? 'both'
            : hasCohortAccess
              ? 'cohort'
              : hasIndividualAccess
                ? 'individual'
                : 'none'
        };
      })
      .sort((a, b) => {
        const lastNameA = a.lastName || '';
        const lastNameB = b.lastName || '';
        if (lastNameA !== lastNameB) {
          return lastNameA.localeCompare(lastNameB);
        }
        return (a.firstName || '').localeCompare(b.firstName || '');
      });
  }, [institutionUsers, cohortMemberships]);

  const handleGrantAccess = async (userId) => {
    if (!userId) return;

    setPendingAccessChange({ userId, newAccess: true });
    await updateUserScenarioAccess({
      userId: userId,
      hasAccess: true
    });
    setSelectedNonCohortUser("");
  };

  const handleRevokeAccess = async (userId) => {
    setPendingAccessChange({ userId, newAccess: false });
    await updateUserScenarioAccess({
      userId: userId,
      hasAccess: false
    });
  };

  return (
    <Card bg="gray.800" borderColor="gray.600">
      <Accordion allowToggle>
        <AccordionItem border="none">
          <AccordionButton _hover={{ bg: "gray.700" }} py={4}>
            <Box flex="1" textAlign="left">
              <HStack spacing={3}>
                <Heading size="md" color="white">Scenario Generator Access</Heading>
                <Badge colorScheme="blue" fontSize="sm">
                  {userAccessStatus.filter(u => u.hasAnyAccess).length} with access
                </Badge>
              </HStack>
            </Box>
            <AccordionIcon color="white" />
          </AccordionButton>
          <AccordionPanel pb={4}>
            <VStack spacing={6} align="stretch">
              {/* Help Text */}
              <Alert status="info" size="sm" borderRadius="md">
                <AlertIcon />
                <VStack align="start" spacing={2} flex="1">
                  <Text fontSize="sm" fontWeight="semibold" color="blue.800">
                    Scenario Generator Access Overview:
                  </Text>
                  <Text fontSize="sm" color="blue.800">
                    This section shows ALL users and their scenario access status. Access can come from:
                  </Text>
                  <VStack align="start" spacing={1} pl={4}>
                    <Text fontSize="xs" color="blue.700">• <strong>Cohort Access:</strong> Granted through cohort membership settings</Text>
                    <Text fontSize="xs" color="blue.700">• <strong>Individual Access:</strong> Granted directly to the user (for non-cohort members)</Text>
                  </VStack>
                  <Text fontSize="xs" color="blue.700" fontStyle="italic">
                    Use individual access for institutional staff/faculty who need access but aren't in cohorts.
                  </Text>
                </VStack>
              </Alert>

              {/* All Users Access Status */}
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold" color="white">
                    All Institution Members:
                  </Text>
                  <HStack spacing={2}>
                    <Badge colorScheme="green" variant="subtle">
                      {userAccessStatus.filter(u => u.hasCohortAccess).length} cohort access
                    </Badge>
                    <Badge colorScheme="purple" variant="subtle">
                      {userAccessStatus.filter(u => u.hasIndividualAccess).length} individual access
                    </Badge>
                    <Badge colorScheme="gray" variant="subtle">
                      {userAccessStatus.filter(u => !u.hasAnyAccess).length} no access
                    </Badge>
                  </HStack>
                </HStack>

                {loadingUsers || loadingMemberships ? (
                  <HStack spacing={2} justify="center" py={4}>
                    <Spinner size="sm" color="blue.400" />
                    <Text fontSize="sm" color="gray.400">Loading access information...</Text>
                  </HStack>
                ) : userAccessStatus.length === 0 ? (
                  <Alert status="info" size="sm">
                    <AlertIcon />
                    <Text fontSize="sm">No users found for this institution.</Text>
                  </Alert>
                ) : (
                  <VStack align="stretch" spacing={2} maxH="400px" overflowY="auto">
                    {userAccessStatus.map(user => (
                      <HStack
                        key={user.id}
                        justify="space-between"
                        p={3}
                        bg={user.hasAnyAccess ? "gray.700" : "gray.750"}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={user.hasAnyAccess ? "green.600" : "gray.600"}
                      >
                        <VStack align="start" spacing={0} flex="1">
                          <Text fontSize="sm" fontWeight="medium" color="white">
                            {user.lastName ? `${user.lastName}, ${user.firstName}` : user.firstName || user.email}
                          </Text>
                          <Text fontSize="xs" color="gray.400">{user.email}</Text>
                          <HStack spacing={2} mt={1}>
                            {user.hasCohortAccess && (
                              <Badge size="xs" colorScheme="green">Cohort Access</Badge>
                            )}
                            {user.hasIndividualAccess && (
                              <Badge size="xs" colorScheme="purple">Individual Access</Badge>
                            )}
                            {!user.hasAnyAccess && (
                              <Badge size="xs" colorScheme="gray">No Access</Badge>
                            )}
                          </HStack>
                        </VStack>
                        <HStack>
                          {!user.hasCohortAccess && !user.hasIndividualAccess && (
                            <Button
                              size="sm"
                              colorScheme="green"
                              variant="outline"
                              onClick={() => handleGrantAccess(user.id)}
                              isDisabled={updatingAccess}
                              isLoading={updatingAccess && pendingAccessChange?.userId === user.id}
                            >
                              Grant Individual Access
                            </Button>
                          )}
                          {user.hasIndividualAccess && !user.hasCohortAccess && (
                            <Button
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => handleRevokeAccess(user.id)}
                              isDisabled={updatingAccess}
                              isLoading={updatingAccess && pendingAccessChange?.userId === user.id}
                            >
                              Revoke Individual Access
                            </Button>
                          )}
                          {user.hasCohortAccess && user.hasIndividualAccess && (
                            <VStack spacing={1}>
                              <Text fontSize="xs" color="gray.400" fontStyle="italic">Has both access types</Text>
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleRevokeAccess(user.id)}
                                isDisabled={updatingAccess}
                                isLoading={updatingAccess && pendingAccessChange?.userId === user.id}
                              >
                                Remove Individual Grant
                              </Button>
                            </VStack>
                          )}
                          {user.hasCohortAccess && !user.hasIndividualAccess && (
                            <Text fontSize="xs" color="gray.400" fontStyle="italic">Access via cohort</Text>
                          )}
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </VStack>

              {/* Error Display */}
              {usersError && (
                <Alert status="error" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">Failed to load users: {usersError.message}</Text>
                </Alert>
              )}
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default IndividualScenarioAccessSection;
